/**
 * Articles Page - 자동 발행 블로그
 * Firestore 기반 AI 생성 콘텐츠 자동 발행
 * 
 * Toss 스타일 깔끔한 디자인
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './ArticlesPage.css';

const ArticlesPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState(null);

  // 카테고리 목록 (contentTopics.js 카테고리와 동기화)
  const categories = [
    { id: 'all', name: '전체' },
    { id: '생기부', name: '생기부' },
    { id: '수행평가', name: '수행평가' },
    { id: '학습지', name: '학습지' },
    { id: 'AI활용', name: 'AI 활용' },
    { id: '학급운영', name: '학급운영' },
    { id: '워라밸', name: '워라밸' },
    { id: '학부모상담', name: '학부모상담' },
    { id: '학교행사', name: '학교행사' },
    { id: '생활지도', name: '생활지도' },
    { id: '진로진학', name: '진로진학' },
    { id: '초등', name: '초등' },
    { id: '중등', name: '중등' },
    { id: '고등', name: '고등' },
    { id: '행정', name: '행정' },
    { id: '온라인수업', name: '온라인수업' },
    { id: '교육트렌드', name: '트렌드' },
    { id: '자기계발', name: '자기계발' },
  ];

  // Firestore에서 아티클 가져오기
  useEffect(() => {
    const fetchArticles = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const articlesRef = collection(db, 'blog_posts');
        let q;
        
        if (selectedCategory === 'all') {
          q = query(
            articlesRef,
            where('published', '==', true),
            orderBy('published_at', 'desc'),
            limit(20)
          );
        } else {
          q = query(
            articlesRef,
            where('published', '==', true),
            where('category', '==', selectedCategory),
            orderBy('published_at', 'desc'),
            limit(20)
          );
        }
        
        const snapshot = await getDocs(q);
        const articlesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setArticles(articlesData);
      } catch (err) {
        console.error('아티클 로드 실패:', err);
        setError('콘텐츠를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticles();
  }, [selectedCategory]);

  // 날짜 포맷
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 읽기 시간 계산
  const calculateReadTime = (content) => {
    if (!content) return '1분';
    const wordCount = content.length;
    const minutes = Math.ceil(wordCount / 500); // 분당 500자 기준
    return `${minutes}분`;
  };

  // 미리보기 텍스트 추출 (마크다운 제거)
  const getPreviewText = (content) => {
    if (!content) return '';
    // 마크다운 문법 제거
    let text = content
      .replace(/#{1,6}\s/g, '')  // 헤더
      .replace(/\*\*(.*?)\*\*/g, '$1')  // 볼드
      .replace(/\*(.*?)\*/g, '$1')  // 이탤릭
      .replace(/\[(.*?)\]\(.*?\)/g, '$1')  // 링크
      .replace(/`(.*?)`/g, '$1')  // 코드
      .replace(/\n/g, ' ')  // 줄바꿈
      .trim();
    return text.slice(0, 150) + (text.length > 150 ? '...' : '');
  };

  // 카테고리별 메타 정보
  const getCategoryMeta = () => {
    if (selectedCategory === 'all') {
      return {
        title: '교사 인사이트 | AI가 엄선한 교육 콘텐츠 | 왓퀴즈',
        description: '매일 업데이트되는 교사를 위한 AI 큐레이션 콘텐츠. 생기부 작성법, 수행평가 설계, 학급 운영 노하우까지.',
        keywords: '교사 블로그, 생기부 작성법, 수행평가 팁, AI 교육, 교사 노하우'
      };
    }
    const categoryName = categories.find(c => c.id === selectedCategory)?.name || selectedCategory;
    return {
      title: `${categoryName} | 교사 인사이트 | 왓퀴즈`,
      description: `${categoryName} 관련 교사를 위한 실용 콘텐츠. AI가 엄선한 최신 정보와 노하우를 만나보세요.`,
      keywords: `${categoryName}, 교사 ${categoryName}, ${categoryName} 팁, 교사 노하우`
    };
  };

  const categoryMeta = getCategoryMeta();
  const canonicalUrl = selectedCategory === 'all' 
    ? 'https://whatquiz.co.kr/articles' 
    : `https://whatquiz.co.kr/articles?category=${selectedCategory}`;
  const ogImage = 'https://whatquiz.co.kr/og-image-whatquiz-2025.png';

  return (
    <div className="articles-page">
      <Helmet>
        <title>{categoryMeta.title}</title>
        <meta name="description" content={categoryMeta.description} />
        <meta name="keywords" content={categoryMeta.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={categoryMeta.title} />
        <meta property="og:description" content={categoryMeta.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="왓퀴즈" />
        <meta property="og:locale" content="ko_KR" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={categoryMeta.title} />
        <meta name="twitter:description" content={categoryMeta.description} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Blog 구조화 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "교사 인사이트",
            "description": "AI가 엄선한 교사를 위한 실용 콘텐츠",
            "url": "https://whatquiz.co.kr/articles",
            "publisher": {
              "@type": "Organization",
              "name": "왓퀴즈",
              "url": "https://whatquiz.co.kr",
              "logo": {
                "@type": "ImageObject",
                "url": "https://whatquiz.co.kr/logo.png"
              }
            },
            "blogPost": articles.slice(0, 10).map(article => ({
              "@type": "BlogPosting",
              "headline": article.title || article.metadata?.seoTitle,
              "url": `https://whatquiz.co.kr/articles/${article.id}`,
              "datePublished": article.published_at?.toDate?.()?.toISOString()
            }))
          })}
        </script>
        
        {/* BreadcrumbList 구조화 데이터 */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "홈",
                "item": "https://whatquiz.co.kr"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "인사이트",
                "item": "https://whatquiz.co.kr/articles"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* 히어로 섹션 */}
      <section className="articles-hero">
        <div className="articles-hero-content">
          <span className="articles-hero-badge">AI 큐레이션</span>
          <h1 className="articles-hero-title">교사 인사이트</h1>
          <p className="articles-hero-desc">
            매일 새롭게 업데이트되는 교사를 위한 실용 콘텐츠
          </p>
        </div>
      </section>

      {/* 카테고리 필터 */}
      <section className="articles-filter">
        <div className="articles-filter-container">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`articles-filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      {/* 구분선 */}
      <div className="articles-divider" />

      {/* 아티클 목록 */}
      <section className="articles-list-section">
        <div className="articles-list-container">
          {isLoading ? (
            <>
              <style>{`@keyframes articles-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
              <div className="articles-grid">
                {[...Array(6)].map((_, i) => {
                  const shimmer = {
                    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                    backgroundSize: '400px 100%',
                    animation: 'articles-shimmer 1.5s infinite',
                    borderRadius: '6px',
                  };
                  return (
                    <div key={i} className={`articles-card${i === 0 ? ' featured' : ''}`} style={{ cursor: 'default' }}>
                      <div className="articles-card-content">
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                          <div style={{ ...shimmer, width: '48px', height: '14px' }} />
                          <div style={{ ...shimmer, width: '80px', height: '14px' }} />
                        </div>
                        <div style={{ ...shimmer, width: '90%', height: '20px', marginBottom: '8px' }} />
                        <div style={{ ...shimmer, width: '70%', height: '20px', marginBottom: '16px' }} />
                        <div style={{ ...shimmer, width: '100%', height: '14px', marginBottom: '6px' }} />
                        <div style={{ ...shimmer, width: '80%', height: '14px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : error ? (
            <div className="articles-error">
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>다시 시도</button>
            </div>
          ) : articles.length === 0 ? (
            <div className="articles-empty">
              <div className="articles-empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h10" />
                </svg>
              </div>
              <h3>아직 발행된 콘텐츠가 없습니다</h3>
              <p>곧 새로운 콘텐츠가 업데이트됩니다</p>
            </div>
          ) : (
            <div className="articles-grid">
              {articles.map((article, index) => (
                <article 
                  key={article.id} 
                  className={`articles-card ${index === 0 ? 'featured' : ''}`}
                  onClick={() => navigate(`/articles/${article.id}`)}
                >
                  {index === 0 && (
                    <span className="articles-card-badge">최신</span>
                  )}
                  <div className="articles-card-content">
                    <div className="articles-card-meta">
                      <span className="articles-card-category">{article.category}</span>
                      <span className="articles-card-dot">·</span>
                      <span className="articles-card-date">{formatDate(article.published_at)}</span>
                    </div>
                    <h2 className="articles-card-title">
                      {article.title || article.metadata?.seoTitle || '제목 없음'}
                    </h2>
                    <p className="articles-card-excerpt">
                      {article.excerpt || getPreviewText(article.content)}
                    </p>
                    <div className="articles-card-footer">
                      <span className="articles-card-readtime">
                        {calculateReadTime(article.content)} 읽기
                      </span>
                      <span className="articles-card-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="articles-cta">
        <div className="articles-cta-container">
          <h2 className="articles-cta-title">더 많은 교사 업무를 AI로 자동화하세요</h2>
          <p className="articles-cta-desc">
            생기부 작성, 학습지 제작, 수행평가 설계까지<br />
            왓퀴즈가 도와드립니다
          </p>
          <button 
            className="articles-cta-btn"
            onClick={() => navigate('/pricing')}
          >
            무료로 시작하기
          </button>
        </div>
      </section>
    </div>
  );
};

export default ArticlesPage;
